/* =========================================================
   Diesel wishlist page renderer.

   Reads saved handles from window.DieselWishlist (localStorage) and, for each,
   fetches the product's real PLP card through the Section Rendering API:

       /products/{handle}?section_id=wishlist-card

   The returned card IS card-product (diesel_plp) — hover gallery, colour
   swatches, wishlist heart and the size quick-add panel included — so the
   wishlist grid matches the collection page exactly and its interactions work
   through the theme's delegated card JS. Fetching live also means price/image
   are always current rather than frozen at save time.

   Reconciles on every wishlist change (including cross-tab): removes cards no
   longer saved, fetches cards newly saved, and re-orders to match storage.
   Un-hearting a card here removes it, since the card's own heart toggles the
   same store. Handles that 404 (deleted/unpublished) are pruned.

   Requires assets/wishlist.js (loaded globally in theme.liquid).
   ========================================================= */
(function () {
  'use strict';

  var root = document.querySelector('[data-wishlist-root]');
  if (!root) return;

  var grid = root.querySelector('[data-wishlist-grid]');
  var empty = root.querySelector('[data-wishlist-empty]');
  var loading = root.querySelector('[data-wishlist-loading]');
  var summary = root.querySelector('[data-wishlist-summary]');

  var ROOT_URL = (root.getAttribute('data-root-url') || '/').replace(/\/$/, '');
  var SECTION = 'wishlist-card';

  var inflight = {}; // handle -> Promise, dedupe concurrent fetches

  function cardUrl(handle) {
    return ROOT_URL + '/products/' + encodeURIComponent(handle) + '?section_id=' + SECTION;
  }

  function fetchCard(handle) {
    if (inflight[handle]) return inflight[handle];
    var p = fetch(cardUrl(handle))
      .then(function (r) {
        if (r.status === 404) return { gone: true };
        if (!r.ok) return { error: true };
        return r.text().then(function (html) { return { html: html }; });
      })
      .catch(function () {
        // Network failure is not proof the product is gone — keep the handle.
        return { error: true };
      })
      .then(function (res) {
        delete inflight[handle];
        return res;
      });
    inflight[handle] = p;
    return p;
  }

  function makeItem(handle, cardHTML) {
    var wrap = document.createElement('div');
    wrap.innerHTML = cardHTML;
    // The section response wraps the card in <div id="shopify-section-…">;
    // lift out just the card so it sits directly in the grid <li>.
    var card = wrap.querySelector('.card-wrapper');
    if (!card) return null;
    var li = document.createElement('li');
    li.className = 'grid__item';
    li.setAttribute('data-wishlist-item', handle);
    li.appendChild(card);
    return li;
  }

  function setState(state, n) {
    if (loading) loading.hidden = state !== 'loading';
    if (empty) empty.hidden = state !== 'empty';
    if (grid) grid.hidden = state !== 'grid';
    if (summary) {
      if (state !== 'grid') {
        summary.textContent = '';
      } else {
        var tpl = summary.getAttribute(n === 1 ? 'data-label-one' : 'data-label-other') || '';
        summary.textContent = tpl.replace('{{ count }}', n).replace('{{count}}', n);
      }
    }
  }

  function itemNodes() {
    return Array.prototype.slice.call(grid.querySelectorAll('[data-wishlist-item]'));
  }

  function reconcile() {
    var api = window.DieselWishlist;
    if (!api || !grid) return;

    var saved = api.getAll();

    // Drop cards that are no longer saved (e.g. un-hearted on this page).
    itemNodes().forEach(function (li) {
      if (saved.indexOf(li.getAttribute('data-wishlist-item')) === -1) li.remove();
    });

    if (!saved.length) {
      grid.innerHTML = '';
      setState('empty');
      return;
    }

    var present = itemNodes().map(function (li) { return li.getAttribute('data-wishlist-item'); });
    var toAdd = saved.filter(function (h) { return present.indexOf(h) === -1; });

    if (!toAdd.length) {
      // Only removals/reorder — no fetch needed.
      reorderAndFinish();
      return;
    }

    if (!grid.children.length) setState('loading');

    Promise.all(
      toAdd.map(function (h) {
        return fetchCard(h).then(function (res) { return { handle: h, res: res }; });
      })
    ).then(function (results) {
      var built = {};
      results.forEach(function (r) {
        if (r.res.gone) {
          api.remove(r.handle); // confirmed gone — prune (fires another reconcile)
          return;
        }
        if (r.res.error || !r.res.html) return; // transient — keep handle, skip
        var li = makeItem(r.handle, r.res.html);
        if (li) built[r.handle] = li;
      });
      reorderAndFinish(built);
    });
  }

  // Re-append every card in saved order (existing nodes are moved, not cloned,
  // so their state/bindings survive), then sync hearts/badges and set state.
  function reorderAndFinish(built) {
    var api = window.DieselWishlist;
    built = built || {};

    var existing = {};
    itemNodes().forEach(function (li) {
      existing[li.getAttribute('data-wishlist-item')] = li;
    });

    var frag = document.createDocumentFragment();
    api.getAll().forEach(function (h) {
      var li = existing[h] || built[h];
      if (li) frag.appendChild(li);
    });

    grid.innerHTML = '';
    grid.appendChild(frag);

    var shown = grid.querySelectorAll('[data-wishlist-item]').length;
    setState(shown ? 'grid' : 'empty', shown);

    // Cards render with aria-pressed="false"; every card here is saved, so
    // re-run the heart/badge sync to fill them red.
    if (api.syncUI) api.syncUI();
  }

  document.addEventListener('diesel:wishlist:change', reconcile);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
